import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { get, isAuthenticated, post } from './services/api';
import { useUI } from './UIContext';
import { handleErr } from './utils/functions';
import { DEFAULT_RESOLVED_THEME, THEME_STORAGE_KEY, THEMES, normalizeTheme } from './constants/theme';

const ThemeContext = createContext();

function getSystemTheme() {
    if (!window.matchMedia) {
        return THEMES.LIGHT;
    }

    return window.matchMedia('(prefers-color-scheme: dark)').matches ? THEMES.DARK : THEMES.LIGHT;
}

function getStoredTheme() {
    try {
        return normalizeTheme(localStorage.getItem(THEME_STORAGE_KEY));
    } catch {
        return THEMES.SYSTEM;
    }
}

function persistTheme(theme) {
    try {
        localStorage.setItem(THEME_STORAGE_KEY, normalizeTheme(theme));
    } catch {
        // Ignore storage failures to avoid breaking theme updates.
    }
}

function applyThemeToDocument(theme) {
    const selectedTheme = normalizeTheme(theme);
    const resolvedTheme = selectedTheme === THEMES.SYSTEM
        ? getSystemTheme()
        : selectedTheme;

    document.documentElement.setAttribute('data-theme', selectedTheme);
    document.documentElement.setAttribute('data-theme-resolved', resolvedTheme);

    return resolvedTheme;
}

export function ThemeProvider({ children }) {
    const { setLoading, setMessage } = useUI();
    const [theme, setTheme] = useState(() => getStoredTheme());
    const [resolvedTheme, setResolvedTheme] = useState(DEFAULT_RESOLVED_THEME);
    const [isThemeReady, setIsThemeReady] = useState(() => !isAuthenticated());
    const hasLoadedRef = useRef(false);
    const revisionRef = useRef(0);
    const sessionRef = useRef(0);
    const saveQueueRef = useRef(Promise.resolve());

    const loadTheme = useCallback(async (force = false, withLoading = true) => {
        if (!isAuthenticated()) {
            hasLoadedRef.current = false;
            setIsThemeReady(true);
            return;
        }

        if (window.location.pathname === '/api-unavailable') {
            setIsThemeReady(true);
            return;
        }

        if (hasLoadedRef.current && !force) {
            setIsThemeReady(true);
            return;
        }

        if (withLoading) {
            setLoading(true);
        }

        setIsThemeReady(false);

        const revision = ++revisionRef.current;
        await get('/user-config/theme', true, setMessage,
            (response) => {
                if (revision !== revisionRef.current) return;
                const apiTheme = normalizeTheme(response?.data?.theme);
                setTheme(apiTheme);
                hasLoadedRef.current = true;
                setIsThemeReady(true);
            },
            (err) => {
                if (revision !== revisionRef.current) return;
                handleErr(setMessage, `Falha ao obter tema do utilizador: ${err}`);
                setIsThemeReady(true);
            },
            () => {
                if (revision === revisionRef.current) setIsThemeReady(true);
                if (withLoading) {
                    setLoading(false);
                }
            },
            'none', 'none', 'none'
        );
    }, [setLoading, setMessage]);

    const saveTheme = useCallback((nextTheme) => {
        const normalizedTheme = normalizeTheme(nextTheme);
        const revision = ++revisionRef.current;
        const session = sessionRef.current;
        setTheme(normalizedTheme);
        persistTheme(normalizedTheme);
        setIsThemeReady(true);

        // Serialize writes and skip superseded selections that have not been sent.
        saveQueueRef.current = saveQueueRef.current.catch(() => {}).then(async () => {
            if (session !== sessionRef.current || revision !== revisionRef.current || !isAuthenticated()) return;
            const reportError = () => {
                if (session === sessionRef.current && revision === revisionRef.current) {
                    setMessage({ type: 'warning', text: 'Tema aplicado neste dispositivo, mas não foi possível salvar no servidor. Selecione o tema novamente para tentar.' });
                }
            };
            try {
                await post('/user-config/theme', { theme: normalizedTheme }, true, reportError,
                    () => {}, reportError, () => {}, 'logout', 'none', 'none');
            } catch {
                reportError();
            }
        });
        return saveQueueRef.current;
    }, [setMessage]);

    useEffect(() => {
        loadTheme();
        return () => {
            revisionRef.current += 1;
            sessionRef.current += 1;
        };
    }, [loadTheme]);

    useEffect(() => {
        function onAuthTokenChanged() {
            revisionRef.current += 1;
            sessionRef.current += 1;
            if (!isAuthenticated()) {
                hasLoadedRef.current = false;
                setIsThemeReady(true);
                return;
            }

            loadTheme(true, false);
        }

        window.addEventListener('auth-token-changed', onAuthTokenChanged);

        return () => {
            window.removeEventListener('auth-token-changed', onAuthTokenChanged);
        };
    }, [loadTheme]);

    useEffect(() => {
        const currentResolvedTheme = applyThemeToDocument(theme);
        persistTheme(theme);
        setResolvedTheme(currentResolvedTheme);
    }, [theme]);

    useEffect(() => {
        if (theme !== THEMES.SYSTEM || !window.matchMedia) {
            return;
        }

        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const onSystemThemeChanged = () => {
            const currentResolvedTheme = applyThemeToDocument(THEMES.SYSTEM);
            setResolvedTheme(currentResolvedTheme);
        };

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', onSystemThemeChanged);
        } else {
            mediaQuery.addListener(onSystemThemeChanged);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', onSystemThemeChanged);
            } else {
                mediaQuery.removeListener(onSystemThemeChanged);
            }
        };
    }, [theme]);

    const contextValue = useMemo(() => ({
        theme,
        resolvedTheme,
        isThemeReady,
        saveTheme,
        loadTheme,
    }), [theme, resolvedTheme, isThemeReady, saveTheme, loadTheme]);

    return (
        <ThemeContext.Provider value={contextValue}>
            {children}
        </ThemeContext.Provider>
    );
}

export function useTheme() {
    return useContext(ThemeContext);
}
